import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { Eye, Edit2, Trash2, MoreVertical, Users, Wrench, FileText as DocumentIcon, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../utils/axios';

// Pure component for vehicle avatar with memoization
const VehicleAvatar = memo(({ photo, name, status }) => {
  const getInitials = useCallback((name) => {
    if (!name) return 'V';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  }, []);

  const statusColorMap = {
    active: 'bg-green-500',
    maintenance: 'bg-yellow-500',
    inactive: 'bg-gray-500',
    default: 'bg-gray-400'
  };

  return (
    <div className="vehicle-avatar">
      {photo ? (
        <img 
          src={photo} 
          alt={name} 
          loading="lazy"
          decoding="async"
          width="40"
          height="40"
        />
      ) : (
        <span className="initials">{getInitials(name)}</span>
      )}
      <span className={`status-badge ${statusColorMap[status] || statusColorMap.default}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    </div>
  );
});

// Pure component for vehicle details
const VehicleDetails = memo(({ vehicle }) => (
  <div className="vehicle-card-body">
    <div className="vehicle-detail">
      <span>Type</span>
      <strong>{vehicle.type}</strong>
    </div>
    <div className="vehicle-detail">
      <span>Capacity</span>
      <strong>{vehicle.capacity?.value || '-'} {vehicle.capacity?.unit || ''}</strong>
    </div>
    <div className="vehicle-detail">
      <span>Zone</span>
      <strong>{vehicle.assignedZone?.name || 'Unassigned'}</strong>
    </div>
  </div>
));

// Pure component for vehicle actions
const VehicleActions = memo(({ 
  vehicle, 
  onViewDetails, 
  onEdit, 
  onDelete, 
  onAssign, 
  onMaintenance, 
  onDocument,
  activeDropdown,
  toggleDropdown
}) => {
  const dropdownRef = useRef(null);

  const handleAction = useCallback((handler) => {
    handler(vehicle);
    toggleDropdown(null);
  }, [vehicle, toggleDropdown]);

  return (
    <div className="dropdown-container" ref={dropdownRef}>
      <button 
        className="more-actions-btn" 
        onClick={() => toggleDropdown(vehicle._id)}
        aria-label="More actions"
      >
        <MoreVertical size={18} />
      </button>
      {activeDropdown === vehicle._id && (
        <div className="dropdown-menu">
          <button onClick={() => handleAction(onViewDetails)}><Eye size={16} /> View</button>
          <button onClick={() => handleAction(onEdit)}><Edit2 size={16} /> Edit</button>
          <button onClick={() => handleAction(onAssign)}><Users size={16} /> Assign</button>
          <button onClick={() => handleAction(onMaintenance)}><Wrench size={16} /> Maintenance</button>
          <button onClick={() => handleAction(onDocument)}><DocumentIcon size={16} /> Documents</button>
          <button onClick={() => onDelete(vehicle._id)} className="delete"><Trash2 size={16} /> Delete</button>
        </div>
      )}
    </div>
  );
});

// Main VehicleCard component
const VehicleCard = memo((props) => {
  return (
    <div className="vehicle-card">
      <div className="vehicle-card-header">
        <VehicleAvatar photo={props.vehicle.photo} name={props.vehicle.name} status={props.vehicle.status} />
        <div className="vehicle-info">
          <h3>{props.vehicle.name}</h3>
          <p>ID: {props.vehicle.vehicleId}</p>
        </div>
        <VehicleActions {...props} />
      </div>
      <VehicleDetails vehicle={props.vehicle} />
      <div className="vehicle-card-footer">
        <button 
          className="action-btn" 
          onClick={() => props.onMaintenance(props.vehicle)}
          title="Maintenance History"
        >
          <Wrench size={16} /> History
        </button>
        <button 
          className="action-btn primary" 
          onClick={() => props.onAssign(props.vehicle)}
          title="Assign Vehicle"
        >
          <Users size={16} /> Assign
        </button>
      </div>
    </div>
  );
});

// Prop type validations
VehicleCard.propTypes = {
  vehicle: PropTypes.object.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
  onMaintenance: PropTypes.func.isRequired,
  onDocument: PropTypes.func.isRequired,
  activeDropdown: PropTypes.string,
  toggleDropdown: PropTypes.func.isRequired
};

// Main VehicleTable component
const VehicleTable = memo(({ 
  vehicles = [], 
  onViewDetails, 
  onEdit, 
  onDelete, 
  onAssign, 
  onMaintenance, 
  onDocument, 
  viewMode = 'table' 
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  const toggleDropdown = useCallback((vehicleId) => {
    setActiveDropdown(prev => prev === vehicleId ? null : vehicleId);
  }, []);

  const memoizedVehicles = useMemo(() => vehicles, [vehicles]);

  if (viewMode === 'grid') {
    return (
      <div className="vehicle-grid">
        {memoizedVehicles.map(vehicle => (
          <VehicleCard
            key={vehicle._id}
            vehicle={vehicle}
            onViewDetails={onViewDetails}
            onEdit={onEdit}
            onDelete={onDelete}
            onAssign={onAssign}
            onMaintenance={onMaintenance}
            onDocument={onDocument}
            activeDropdown={activeDropdown}
            toggleDropdown={toggleDropdown}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="management-table">
        <thead>
          <tr>
            <th>Vehicle</th>
            <th>Type</th>
            <th>Capacity</th>
            <th>Zone</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {memoizedVehicles.map(vehicle => (
            <tr key={vehicle._id}>
              <td>
                <div className="worker-info-cell">
                  <VehicleAvatar photo={vehicle.photo} name={vehicle.name} status={vehicle.status} />
                  <div className="worker-details">
                    <span className="worker-name">{vehicle.name}</span>
                    <span className="worker-id">{vehicle.vehicleId}</span>
                  </div>
                </div>
              </td>
              <td>{vehicle.type}</td>
              <td>{vehicle.capacity?.value} {vehicle.capacity?.unit}</td>
              <td>{vehicle.zone?.name || 'N/A'}</td>
              <td>
                <div className="status-dropdown-container">
                  <button className={`status-badge ${vehicle.status}`} onClick={() => toggleDropdown(vehicle._id)}>
                    {vehicle.status}
                    <ChevronDown size={16} />
                  </button>
                  {activeDropdown === vehicle._id && (
                    <div className="status-options">
                      <button onClick={() => onEdit(vehicle)}>Active</button>
                      <button onClick={() => onEdit(vehicle)}>Inactive</button>
                      <button onClick={() => onEdit(vehicle)}>Maintenance</button>
                    </div>
                  )}
                </div>
              </td>
              <td>
                <div className="actions-cell">
                  <button className="action-btn" onClick={() => onViewDetails(vehicle)} title="View Details">
                    <Eye size={18} />
                  </button>
                  <button className="action-btn" onClick={() => onEdit(vehicle)} title="Edit Vehicle">
                    <Edit2 size={18} />
                  </button>
                  <button className="action-btn delete" onClick={() => onDelete(vehicle._id)} title="Delete Vehicle">
                    <Trash2 size={18} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

// Prop type validations
VehicleTable.propTypes = {
  vehicles: PropTypes.arrayOf(PropTypes.object).isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
  onMaintenance: PropTypes.func.isRequired,
  onDocument: PropTypes.func.isRequired,
  viewMode: PropTypes.string
};

export default VehicleTable;